import {
    type InfiniteData,
    type QueryClient,
    useInfiniteQuery,
    useQuery,
    useQueryClient,
} from "@tanstack/react-query";
import { createServerFn } from "@tanstack/react-start";
import {
    type DiscoverySearchResult,
    discoverySearchParamsSchema,
    type ParsedSearchIntentV2,
    type SearchExecutionContext,
    type SearchTerminalContext,
} from "@wags/discovery/index.ts";
import { PARSED_SEARCH_MAX_FUSED_CANDIDATES } from "@wags/discovery/search/parsed-search.contract.ts";
import type { RankingDecision } from "@wags/discovery/search/ranking-decision.contract.ts";

import type { FindFeedRetryOptions } from "./find/find-load-failure";
import { resolveViewerProfileIdentity } from "@/lib/core/auth";
import {
    type CollectionWagsListing,
    upsertWagsListingSummaries,
    useWagsListingsLiveQuery,
} from "@/features/listings";
import {
    type CompatibilityScoreSidecars,
    getCompatibilityScoreSidecarsForListings,
} from "@/features/matching";
import type {
    SearchParams,
    SearchResult,
    SearchResultItem,
} from "@/features/discovery";
import {
    buildDiscoverySearchRequest as buildSearchRequest,
    correlatedSearchInputSchema,
    getRememberedParsedSearchIntent,
    listingInterpretationsFromParsed,
    resolveParsedSearchIntentV2Preview,
    shouldUseParsedListingSearch,
    withSeekerLocationOnListingIntents,
    searchPrefetchTracker,
    type SearchPrefetchSource,
} from "@/features/discovery";
import { getDiscovery } from "../../lib/core/discovery-core.server.ts";
import { decideRankingForTrustedProfile } from "@/features/discovery/index.server";

const DEFAULT_LIMIT = 50;
const noopFetchNextPage = () => {};
const noopRefetch = () => {};

type SearchProjectionPage = SearchResult & {
    intentIndexesByResultId?: Record<string, number[]>;
    branchExecutions?: Array<{
        executionId: string;
        intentIndex: number;
        target: "listings" | "organizations";
    }>;
};
type SearchPresentationPage = SearchProjectionPage & {
    presentationGenerationId: string;
};

const presentationGenerationBySearchPage = new WeakMap<object, string>();

export function getSearchPagePresentationGeneration(
    page: SearchProjectionPage,
) {
    const existing = presentationGenerationBySearchPage.get(page);
    if (existing) return existing;
    const generation = crypto.randomUUID();
    presentationGenerationBySearchPage.set(page, generation);
    return generation;
}

type SearchResultData = {
    result: SearchProjectionPage;
    listingDocuments?: import("@wags/db/v2").WagsListingDocument[];
};

function rankingSearchInput(decision?: RankingDecision) {
    if (decision?.status !== "randomized") return {};
    return {
        rankingAlgorithm:
            decision.arm === "control"
                ? ("legacy-ring" as const)
                : ("progressive-pool" as const),
    };
}

function withRankingDecision(
    execution?: SearchExecutionContext,
    decision?: RankingDecision,
): SearchExecutionContext | undefined {
    if (!execution || !decision) return execution;
    return { ...execution, rankingDecision: decision };
}

export type SearchBundledResult = {
    result: SearchProjectionPage;
    sidecars: {
        compatibilityScores: CompatibilityScoreSidecars;
    };
};

export function indexSearchResultIntentIndexes(
    results: Array<{ entityId: string; intentIndexes: number[] }>,
) {
    return Object.fromEntries(
        results.map(({ entityId, intentIndexes }) => [entityId, intentIndexes]),
    );
}

function sanitizeSearchParams(params: SearchParams): SearchParams {
    return {
        lat: params.lat,
        lng: params.lng,
        q: params.q,
        feedContext: params.feedContext,
        maxDistanceMiles: params.maxDistanceMiles,
        hardRadius: params.hardRadius,
        breedSlugs: params.breedSlugs,
        dogName: params.dogName,
        breedType: params.breedType,
        providerName: params.providerName,
        providerType: params.providerType,
        size: params.size,
        coatType: params.coatType,
        sheddingLevel: params.sheddingLevel,
        hypoallergenic: params.hypoallergenic,
        priceMin: params.priceMin,
        priceMax: params.priceMax,
        dobMin: params.dobMin,
        dobMax: params.dobMax,
        sex: params.sex,
        friendlinessChildren: params.friendlinessChildren,
        friendlinessDogs: params.friendlinessDogs,
        friendlinessCats: params.friendlinessCats,
        energyLevel: params.energyLevel,
        trainability: params.trainability,
        healthIssuesRisk: params.healthIssuesRisk,
        excludeLitters: params.excludeLitters,
        sortBy: params.sortBy,
        bestMatchPreferences: params.bestMatchPreferences,
        offset: params.offset,
        page: params.page,
        limit: params.limit,
    };
}

function normalizeInfiniteSearchParams(params: SearchParams): SearchParams {
    const normalizedParams = sanitizeSearchParams(params);

    return {
        ...normalizedParams,
        page: 0,
        limit: normalizedParams.limit ?? DEFAULT_LIMIT,
    };
}

function getInfiniteSearchQueryKey(params: SearchParams) {
    return ["infiniteSearch", normalizeInfiniteSearchParams(params)] as const;
}

export function getInfiniteSearchPrefetchKey(params: SearchParams) {
    return JSON.stringify(normalizeInfiniteSearchParams(params));
}

export function copyFreshWarmSearchToSubmission(
    queryClient: QueryClient,
    params: SearchParams,
    submissionId: string,
    now = Date.now(),
) {
    const normalizedParams = normalizeInfiniteSearchParams(params);
    const submittedKey = [
        ...getInfiniteSearchQueryKey(normalizedParams),
        submissionId,
    ];
    if (queryClient.getQueryData(submittedKey) !== undefined) return false;
    const warmKey = [...getInfiniteSearchQueryKey(normalizedParams), null];
    const warmState = queryClient.getQueryState(warmKey);
    const warmData =
        queryClient.getQueryData<InfiniteData<SearchProjectionPage, number>>(
            warmKey,
        );
    if (
        !warmData ||
        !warmState ||
        now - warmState.dataUpdatedAt >= 5 * 60 * 1_000
    ) {
        return false;
    }
    queryClient.setQueryData(submittedKey, warmData);
    searchPrefetchTracker.adoptWarmCache(
        JSON.stringify(normalizedParams),
        submissionId,
        warmState.dataUpdatedAt,
    );
    return true;
}

async function resolveListingParsedSearch(
    params: SearchParams,
    parsed?: ParsedSearchIntentV2,
): Promise<ParsedSearchIntentV2 | undefined> {
    let source = parsed;
    if (!source && shouldUseParsedListingSearch(params) && params.q) {
        try {
            const resolved = await resolveParsedSearchIntentV2Preview(params.q);
            if (resolved.available) source = resolved.parsed;
        } catch {
            return undefined;
        }
    }
    if (!source) return undefined;
    const listingIntents = listingInterpretationsFromParsed(source);
    if (listingIntents.length === 0) return undefined;
    return withSeekerLocationOnListingIntents(
        { intents: listingIntents },
        params,
    );
}

function rememberedParsedForSearch(params: SearchParams) {
    return shouldUseParsedListingSearch(params)
        ? getRememberedParsedSearchIntent(params.q)
        : undefined;
}

function toSearchResultItem(
    projection: SearchResultItem,
    listing: CollectionWagsListing | undefined,
): SearchResultItem {
    const breeds = listing?.breeds.length
        ? listing.breeds.map((breed) => ({
              id: breed.slug || breed.id,
              name: breed.name,
          }))
        : projection.breeds;

    return {
        id: projection.id,
        name: listing?.name ?? projection.name,
        location: listing?.location ?? projection.location,
        birthday: listing?.birthday ?? projection.birthday,
        ageLabel: listing?.ageLabel ?? projection.ageLabel,
        weightBucket: listing?.weightBucket ?? projection.weightBucket,
        sex: listing?.sex ?? projection.sex,
        ...toSearchResultMedia(projection, listing),
        breeds,
        distanceMiles: projection.distanceMiles,
        ...toSearchResultDetails(projection, listing),
    };
}

function toSearchResultMedia(
    projection: SearchResultItem,
    listing: CollectionWagsListing | undefined,
) {
    return {
        imageUrls: listing?.imageUrls.length
            ? listing.imageUrls
            : projection.imageUrls,
        availableAt: listing?.availableAt ?? projection.availableAt,
    };
}

function toSearchResultDetails(
    projection: SearchResultItem,
    listing: CollectionWagsListing | undefined,
) {
    return {
        providerType: listing?.providerType ?? projection.providerType,
        minPrice: listing?.priceRange?.min ?? projection.minPrice,
        maxPrice: listing?.priceRange?.max ?? projection.maxPrice,
        description: listing?.description ?? projection.description,
    };
}

async function fetchSearchProjectionPage(
    dbClient: DbClient,
    params: SearchParams,
    page: number,
    correlation?: SearchTerminalContext,
    signal?: AbortSignal,
    onNetworkStart?: () => void,
): Promise<SearchProjectionPage> {
    const normalizedParams = {
        ...normalizeInfiniteSearchParams(params),
        page,
    };
    onNetworkStart?.();
    const response = correlation
        ? await searchWithCompatibilityScoresCorrelated({
              data: {
                  params: normalizedParams,
                  context: correlation,
                  parsed: rememberedParsedForSearch(normalizedParams),
              },
              signal,
          })
        : await searchWithCompatibilityScores({
              data: normalizedParams,
              signal,
          });

    if (typeof window !== "undefined") {
        upsertWagsListingSummaries(dbClient, response.result.results);
    }

    return response.result;
}

async function fetchSearchResultData(
    data: SearchParams,
    options: { includeListingDocuments?: boolean } = {},
    execution?: SearchExecutionContext,
    parsed?: ParsedSearchIntentV2,
    terminalContext?: SearchTerminalContext,
    rankingDecision?: RankingDecision,
): Promise<SearchResultData> {
    const { page, limit, offset, input } = buildSearchRequest(data);
    const discovery = await getDiscovery();
    const listingParsed = await resolveListingParsedSearch(data, parsed);
    if (listingParsed) {
        const parsedResult = await discovery.search.searchParsed(
            {
                parsed: listingParsed,
                limit,
                offset,
                sortBy: data.sortBy,
                ...(options.includeListingDocuments
                    ? { includeListingDocuments: true }
                    : {}),
            },
            terminalContext,
            rankingDecision,
            execution?.executionId,
        );
        const listings = parsedResult.listings;
        if (!listings) {
            throw new Error("Parsed listing search returned no listing page");
        }
        const listingDocuments = parsedResult.listingDocuments ?? [];
        return {
            result: {
                anchorIndex: null,
                results: listings.results.map((entry) => entry.entity),
                intentIndexesByResultId: indexSearchResultIntentIndexes(
                    listings.results,
                ),
                branchExecutions: parsedResult.branches.flatMap((branch) =>
                    branch.executionId
                        ? [
                              {
                                  executionId: branch.executionId,
                                  intentIndex: branch.intentIndex,
                                  target: branch.target,
                              },
                          ]
                        : [],
                ),
                offset: listings.offset,
                page,
                limit,
                total: listings.candidateCount,
                prevOffset:
                    listings.offset > 0
                        ? Math.max(0, listings.offset - limit)
                        : null,
                nextOffset: listings.hasNextPage
                    ? listings.offset + listings.results.length
                    : null,
                nextPage:
                    listings.hasNextPage &&
                    (page + 2) * limit <= PARSED_SEARCH_MAX_FUSED_CANDIDATES
                        ? page + 1
                        : null,
                withinRangeCount: null,
                execution,
            },
            ...(listingDocuments.length > 0 ? { listingDocuments } : {}),
        };
    }

    const wagsResults = await discovery.search.search(
        {
            ...input,
            ...rankingSearchInput(rankingDecision),
            includeListingDocuments: options.includeListingDocuments,
        },
        undefined,
        withRankingDecision(execution, rankingDecision),
    );

    const total = wagsResults.total;

    const result = {
        anchorIndex: null as number | null,
        results: wagsResults.listings,
        offset: wagsResults.offset,
        page,
        limit,
        total,
        prevOffset:
            wagsResults.offset > 0
                ? Math.max(0, wagsResults.offset - limit)
                : null,
        nextOffset:
            wagsResults.offset + wagsResults.listings.length < total
                ? wagsResults.offset + wagsResults.listings.length
                : null,
        nextPage:
            (wagsResults.hasNextPage ??
            offset + wagsResults.listings.length < total)
                ? page + 1
                : null,
        withinRangeCount: wagsResults.withinRangeCount,
        execution,
    } satisfies DiscoverySearchResult & {
        execution?: SearchExecutionContext;
    };

    return {
        result,
        ...(wagsResults.listingDocuments
            ? { listingDocuments: wagsResults.listingDocuments }
            : {}),
    };
}

export const searchWithCompatibilityScores = createServerFn({ method: "GET" })
    .inputValidator((data: SearchParams) =>
        discoverySearchParamsSchema.parse(data),
    )
    .handler(async ({ data }): Promise<SearchBundledResult> => {
        const auth = await resolveViewerProfileIdentity();
        const rankingDecision = await decideRankingForTrustedProfile(
            auth?.appUserId ?? null,
        );
        const { page, limit } = buildSearchRequest(data);
        const execution: SearchExecutionContext = {
            searchInteractionId: crypto.randomUUID(),
            submissionId: crypto.randomUUID(),
            queryRevision: 0,
            executionId: crypto.randomUUID(),
            operation: page === 0 ? "results" : "page",
            target: "listings",
            intentIndexes: [0],
            page,
            pageSize: limit,
        };
        const internalTerminal: SearchTerminalContext = {
            searchInteractionId: execution.searchInteractionId,
            submissionId: execution.submissionId,
            queryRevision: 0,
            query: "",
            submissionSource: "api",
            action: "api",
            surface: "web",
        };
        const { result, listingDocuments } = await fetchSearchResultData(
            data,
            {
                includeListingDocuments: true,
            },
            execution,
            undefined,
            internalTerminal,
            rankingDecision,
        );
        const compatibilityScores = auth
            ? await getCompatibilityScoreSidecarsForListings({
                  listingDocuments: listingDocuments ?? [],
                  userId: auth.appUserId,
                  isAnonymous: !auth.isAuthenticated,
              })
            : null;

        return {
            result,
            sidecars: { compatibilityScores },
        };
    });

export const searchWithCompatibilityScoresCorrelated = createServerFn({
    method: "POST",
})
    .inputValidator((data) => correlatedSearchInputSchema.parse(data))
    .handler(async ({ data }): Promise<SearchBundledResult> => {
        const { page, limit } = buildSearchRequest(data.params);
        const listingCount = data.parsed
            ? listingInterpretationsFromParsed(data.parsed).length
            : 1;
        const execution: SearchExecutionContext = {
            ...data.context,
            executionId: crypto.randomUUID(),
            operation: page === 0 ? "results" : "page",
            target: "listings",
            intentIndexes:
                listingCount > 0
                    ? Array.from({ length: listingCount }, (_, index) => index)
                    : [0],
            page,
            pageSize: limit,
        };
        const auth = await resolveViewerProfileIdentity();
        const rankingDecision = await decideRankingForTrustedProfile(
            auth?.appUserId ?? null,
        );
        const { result, listingDocuments } = await fetchSearchResultData(
            data.params,
            { includeListingDocuments: true },
            execution,
            data.parsed,
            data.context,
            rankingDecision,
        );
        const compatibilityScores = auth
            ? await getCompatibilityScoreSidecarsForListings({
                  listingDocuments: listingDocuments ?? [],
                  userId: auth.appUserId,
                  isAnonymous: !auth.isAuthenticated,
              })
            : null;

        return { result, sidecars: { compatibilityScores } };
    });

export const getCorrelatedMatchedDogCount = createServerFn({
    method: "POST",
})
    .inputValidator((data) => correlatedSearchInputSchema.parse(data))
    .handler(async ({ data }) => {
        const { page, limit, input } = buildSearchRequest(data.params);
        const listingCount = data.parsed
            ? listingInterpretationsFromParsed(data.parsed).length
            : 1;
        const execution: SearchExecutionContext = {
            ...data.context,
            executionId: crypto.randomUUID(),
            operation: "count",
            target: "listings",
            intentIndexes:
                listingCount > 0
                    ? Array.from({ length: listingCount }, (_, index) => index)
                    : [0],
            page,
            pageSize: limit,
        };
        const listingParsed = await resolveListingParsedSearch(
            data.params,
            data.parsed,
        );
        if (listingParsed) {
            const listings = (
                await (
                    await getDiscovery()
                ).search.searchParsed(
                    {
                        parsed: listingParsed,
                        limit: 1,
                        offset: 0,
                    },
                    data.context,
                )
            ).listings;
            return { count: listings?.candidateCount ?? 0, execution };
        }
        const discovery = await getDiscovery();
        const count = await discovery.search.countMatched(input, execution);
        return { count, execution };
    });

function getInfiniteSearchQueryOptions(
    dbClient: DbClient,
    params: SearchParams,
    correlation?: SearchTerminalContext,
    signal?: AbortSignal,
    retryOptions?: FindFeedRetryOptions,
    onNetworkStart?: () => void,
) {
    const normalizedParams = normalizeInfiniteSearchParams(params);

    return {
        queryKey: [
            ...getInfiniteSearchQueryKey(normalizedParams),
            correlation?.submissionId ?? null,
        ],
        initialPageParam: 0,
        queryFn: async ({ pageParam }: { pageParam: number }) =>
            await fetchSearchProjectionPage(
                dbClient,
                normalizedParams,
                pageParam,
                correlation,
                signal,
                onNetworkStart,
            ),
        getNextPageParam: (lastPage: SearchProjectionPage) => lastPage.nextPage,
        placeholderData: (
            previousData:
                | InfiniteData<SearchProjectionPage, number>
                | undefined,
        ) => previousData,
        staleTime: 5 * 60 * 1000,
        ...retryOptions,
    };
}

export async function prefetchInfiniteSearch(
    queryClient: QueryClient,
    dbClient: DbClient,
    params: SearchParams,
    correlation?: SearchTerminalContext,
    signal?: AbortSignal,
    retryOptions?: FindFeedRetryOptions,
    onNetworkStart?: () => void,
) {
    const options = getInfiniteSearchQueryOptions(
        dbClient,
        params,
        correlation,
        signal,
        retryOptions,
        onNetworkStart,
    );
    if (correlation) {
        copyFreshWarmSearchToSubmission(
            queryClient,
            params,
            correlation.submissionId,
        );
    }

    return await queryClient.ensureInfiniteQueryData(options);
}

export function useInfiniteSearch(
    params: SearchParams | undefined,
    correlation?: SearchTerminalContext,
    retryOptions?: FindFeedRetryOptions,
) {
    const dbClient = useDbClient();
    const {
        data: infiniteData,
        fetchNextPage: fetchInfiniteNextPage,
        hasNextPage,
        isFetching,
        isFetchingNextPage,
        isLoading,
        isPlaceholderData,
        isError,
        isFetchNextPageError,
        isFetchedAfterMount,
        refetch: refetchInfiniteSearch,
    } = useInfiniteQuery({
        ...getInfiniteSearchQueryOptions(
            dbClient,
            params ?? { lat: 0, lng: 0, page: 0, limit: DEFAULT_LIMIT },
            correlation,
            undefined,
            retryOptions,
        ),
        enabled: Boolean(params),
    });
    const rawPages = infiniteData?.pages ?? [];
    const listingIds = rawPages.flatMap((page) =>
        page.results.map((result) => result.id),
    );
    const listingsQuery = useWagsListingsLiveQuery(listingIds);

    if (!params) {
        return {
            data: undefined as
                | InfiniteData<SearchPresentationPage, number>
                | undefined,
            isLoading: false,
            isFetching: false,
            isPlaceholderData: false,
            isError: false,
            isFetchNextPageError: false,
            isFetchedAfterMount: false,
            refetch: noopRefetch,
            fetchNextPage: noopFetchNextPage,
            hasNextPage: false,
            isFetchingNextPage: false,
            hydratedListingIds: new Set<string>(),
        };
    }

    const listingById = new Map(
        (listingsQuery.data ?? []).map(
            (listing) => [listing.id, listing] as const,
        ),
    );
    const hydratedListingIds = new Set(
        (listingsQuery.data ?? [])
            .filter((listing) => listing.detailHydratedAt != null)
            .map((listing) => listing.id),
    );
    const pages = rawPages.map((page) => ({
        ...page,
        presentationGenerationId: getSearchPagePresentationGeneration(page),
        results: page.results.map((projection) =>
            toSearchResultItem(projection, listingById.get(projection.id)),
        ),
    }));

    return {
        data: infiniteData
            ? ({
                  pages,
                  pageParams: infiniteData.pageParams as number[],
              } satisfies InfiniteData<SearchPresentationPage, number>)
            : undefined,
        isLoading,
        isFetching,
        isPlaceholderData,
        isError,
        isFetchNextPageError,
        isFetchedAfterMount,
        refetch: () => refetchInfiniteSearch(),
        fetchNextPage: () => fetchInfiniteNextPage(),
        hasNextPage: Boolean(hasNextPage),
        isFetchingNextPage,
        hydratedListingIds,
    };
}

export function usePrefetchInfiniteSearch(
    params: SearchParams | undefined,
    options: {
        source?: SearchPrefetchSource;
    } = {},
) {
    const dbClient = useDbClient();
    const queryClient = useQueryClient();
    const prefetchKey = params
        ? getInfiniteSearchPrefetchKey(params)
        : undefined;
    const source = options.source ?? "filter_draft";

    useQuery({
        queryKey: ["prefetch-infinite-search", prefetchKey, source],
        queryFn: ({ signal }) =>
            new Promise<null>((resolve) => {
                const normalizedParams = JSON.parse(
                    prefetchKey as string,
                ) as SearchParams;
                const attempt = searchPrefetchTracker.beginAttempt(
                    source,
                    prefetchKey as string,
                );
                const prefetch = () => {
                    const queryOptions = getInfiniteSearchQueryOptions(
                        dbClient,
                        normalizedParams,
                    );
                    const state = queryClient.getQueryState(
                        queryOptions.queryKey,
                    );
                    searchPrefetchTracker.startAttempt(attempt, {
                        hasData:
                            queryClient.getQueryData(queryOptions.queryKey) !==
                            undefined,
                        dataUpdatedAt: state?.dataUpdatedAt ?? 0,
                        isFetching: state?.fetchStatus === "fetching",
                    });
                    void prefetchInfiniteSearch(
                        queryClient,
                        dbClient,
                        normalizedParams,
                        undefined,
                        signal,
                        undefined,
                        () =>
                            searchPrefetchTracker.networkStarted(
                                attempt.attemptId,
                            ),
                    )
                        .then((data) => {
                            searchPrefetchTracker.resolveAttempt(
                                attempt.attemptId,
                                {
                                    dataUpdatedAt: queryClient.getQueryState(
                                        queryOptions.queryKey,
                                    )?.dataUpdatedAt,
                                    resultCount: data.pages.reduce(
                                        (total, page) =>
                                            total + page.results.length,
                                        0,
                                    ),
                                },
                            );
                        })
                        .catch((error) => {
                            if (
                                error instanceof Error &&
                                error.name === "AbortError"
                            ) {
                                searchPrefetchTracker.cancelAttempt(
                                    attempt.attemptId,
                                );
                                return;
                            } else {
                                searchPrefetchTracker.failAttempt(
                                    attempt.attemptId,
                                );
                            }
                            console.warn(
                                "[search] infinite search prefetch failed",
                                {
                                    error:
                                        error instanceof Error
                                            ? {
                                                  message: error.message,
                                                  name: error.name,
                                              }
                                            : error,
                                },
                            );
                        })
                        .finally(() => resolve(null));
                };

                if (typeof window === "undefined" || signal.aborted) {
                    searchPrefetchTracker.cancelAttempt(attempt.attemptId);
                    resolve(null);
                    return;
                }

                prefetch();
            }),
        enabled: Boolean(prefetchKey),
        staleTime: 0,
    });
}

import { type DbClient, useDbClient } from "@tanstack/react-db";
